import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { paymentEntitiesQuery } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  showSnackbar,
  isDuplicatePaymentMethod,
} from '@b-stock/fe-core/bstock-react'
import {
  payAddHandler,
  PayAddHandlerResultStatus,
} from '@b-stock/fe-core/pay-ui'
import {
  PayMethodStatusEnum,
  PayMethodTypeEnum,
} from '@b-stock/payments-methods-api-client'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import {
  getPaymentMethodAddedMessage,
  showPaymentMethodAddedSnackbar,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/helpers/showPaymentMethodAddedSnackbar'
import deletePaymentMethod from '@/buyer-portal/mutations/deletePaymentMethod'
import getAllPaymentMethods from '@/buyer-portal/mutations/getAllPaymentMethods'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useTrackToastView } from '@/helpers/telemetry/useTrackToastView'

const NS = 'BidConfirmationModal.autoPay.modal'

// `showSnackbar` type union does not include 'warning', so failure-path toasts
// render with type:'error'. Status is still reflected in the locale string.
const showFailureSnackbar = (
  key: 'warning.content' | 'error.content' | 'duplicate.content',
  options: { autoClose?: boolean } = {}
): void => {
  showSnackbar({
    content: <FormattedMessage id={`${NS}.add.paymentMethod.${key}`} />,
    options: { type: 'error', ...options },
  })
}

const findNewlyAddedMethod = (
  fetched: PayMethod[],
  existing: PayMethod[]
): PayMethod | undefined =>
  fetched.find((m) => !existing.some((initial) => initial.id === m.id))

type UseHandlePaymentAddedParams = {
  accountId: string
  accessToken: string
  paymentMethods: PayMethod[]
  refetchPaymentMethods: () => Promise<unknown>
  setAddMethodModalOpen: (open: boolean) => void
  setPaymentMethodsLoading: (loading: boolean) => void
  setNewlyAddedMethodId: (id: string) => void
}

export const useHandlePaymentAdded = ({
  accountId,
  accessToken,
  paymentMethods,
  refetchPaymentMethods,
  setAddMethodModalOpen,
  setPaymentMethodsLoading,
  setNewlyAddedMethodId,
}: UseHandlePaymentAddedParams) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  const trackToastView = useTrackToastView()
  const { trackButtonClicked } = useAnalytics()
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)

  return useCallback(async () => {
    setAddMethodModalOpen(false)
    setPaymentMethodsLoading(true)

    // Silent fetch: hits the API directly without updating the React Query cache,
    // so polling inside payAddHandler doesn't cause UI re-renders. The last poll
    // result is captured so we can find the newly added method after success.
    //
    // Mirrors the filter applied by useAutoPaymentMethods so that the fetched
    // list matches the `methods` baseline passed to payAddHandler. Without this,
    // under AUTOPAY_ENFORCED the full wallet returned by getAllPaymentMethods
    // can be larger than the filtered baseline, causing payAddHandler to
    // resolve SUCCESS immediately and findNewlyAddedMethod to pick a
    // pre-existing non-reusable method as the "newly added" one.
    let lastFetchedMethods: PayMethod[] = []
    const silentFetch = async () => {
      const all = await getAllPaymentMethods(accountId, accessToken)
      const data = autoPayEnforced
        ? all.filter(
            (pm) =>
              pm.type === PayMethodTypeEnum.Getbalance ||
              pm.status === PayMethodStatusEnum.Reusable
          )
        : all
      lastFetchedMethods = data
      return { data }
    }

    try {
      const result = await payAddHandler({
        redirectURI: undefined,
        refetch: silentFetch,
        methods: paymentMethods,
        refetchTimeout: 2000,
      })()

      // popup_closed = user cancelled the widget; stay silent (no misleading error).
      if (result.status === PayAddHandlerResultStatus.popup_closed) return

      if (result.status !== PayAddHandlerResultStatus.success) {
        const key =
          result.status === PayAddHandlerResultStatus.warning
            ? 'warning.content'
            : 'error.content'
        showFailureSnackbar(key)
        return
      }

      // payAddHandler only resolves SUCCESS once silentFetch returns a list
      // longer than `methods`, so the freshly-added method is already in
      // lastFetchedMethods at this point.
      const newMethod = findNewlyAddedMethod(lastFetchedMethods, paymentMethods)

      if (newMethod && isDuplicatePaymentMethod(newMethod, paymentMethods)) {
        await deletePaymentMethod(newMethod.id, accessToken)
        await refetchPaymentMethods()
        showFailureSnackbar('duplicate.content', { autoClose: true })
        return
      }

      if (!newMethod) {
        // payAddHandler reported success but the new method never surfaced.
        showFailureSnackbar('warning.content')
        return
      }

      // Only invalidate after we've confirmed a new method exists — avoids
      // an extra refetch when the flow ultimately falls back to a warning.
      await queryClient.invalidateQueries({
        queryKey: [paymentEntitiesQuery.name],
      })

      // Pre-select the newly added method regardless of whether we have
      // enough details to render the descriptive toast.
      setNewlyAddedMethodId(newMethod.id)

      trackButtonClicked({
        buttonName: 'added_new_payment',
        payment_mode: 'ACH',
      })

      if (showPaymentMethodAddedSnackbar(newMethod)) {
        trackToastView('Success', getPaymentMethodAddedMessage(intl, newMethod))
      } else {
        // Method lacked resolvable details for the detailed success copy.
        showFailureSnackbar('warning.content')
      }
    } catch (error) {
      console.error('Unexpected error in payAddHandler', error)
      showFailureSnackbar('error.content', { autoClose: true })
    } finally {
      setPaymentMethodsLoading(false)
    }
  }, [
    accountId,
    accessToken,
    autoPayEnforced,
    paymentMethods,
    queryClient,
    refetchPaymentMethods,
    setAddMethodModalOpen,
    setPaymentMethodsLoading,
    setNewlyAddedMethodId,
    intl,
    trackButtonClicked,
    trackToastView,
  ])
}
